/**
 * Copy for the folder view — the file table (SPEC §6, FIL-4..6, FIL-8).
 *
 * Sentence case throughout: `Eyebrow` and `Badge` apply the uppercase and the
 * wide tracking themselves.
 */
export const FOLDER_COPY = {
  backToFiles: 'Back to folders',
  /** Sits above the folder's name, saying what this screen is. */
  eyebrow: 'Shared Folder',

  backToSpace: 'Back to this shared space',

  /**
   * FIL-4's columns.
   *
   * **No `download` column any more.** Download is the file's title now —
   * `FileNameLink` — matching how a folder's or a space's name is already the
   * way in one level up. `actions` still goes blank on a row whose file
   * another agency uploaded, since that reader has nothing left to reveal
   * behind the overflow trigger unless the file is also archived (§5.3,
   * PERM-4, RET-9).
   *
   * **No `kind` header.** The kind still exists — it prefixes the `name`
   * cell as an icon (`FOLDER_COPY.kinds` still labels it) — but a glyph
   * living inside another column's cell has no header of its own to name.
   */
  columns: {
    name: 'File',
    size: 'Size',
    verification: 'Verification',
    addedBy: 'Added by',
    addedAt: 'Added',
    retention: 'Retention',
    actions: 'Actions',
  },

  card: {
    addedAt: (date: string) => `Added ${date}`,
  },

  /**
   * `kind` is derived from MIME/extension at upload and is **informational, not
   * behavioral** (FIL-4) — nothing branches on it. These are the labels only.
   */
  kinds: {
    PDF: 'PDF',
    VIDEO: 'Video',
    PHOTOS: 'Photos',
    AUDIO: 'Audio',
    ARCHIVE: 'Archive',
    PHONE_DUMP: 'Phone dump',
    DOC: 'Document',
    OTHER: 'Other',
  },

  /**
   * The line under the folder's name, so the reader has its shape before they
   * reach the table: agency, last activity, file count, total size.
   *
   * **A byte total is honest here now.** `useFiles` follows `continuationToken`
   * to exhaustion before it calls a folder loaded (`src/lib/useFiles.ts`), so
   * summing `sizeBytes` over the loaded rows is summing the whole folder, not
   * one page of it.
   */
  summary: {
    files: (count: number) =>
      count === 0 ? 'No files' : count === 1 ? '1 file' : `${String(count)} files`,
    /** The one segment of the summary line that names what it is measuring. */
    lastActivity: (date: string) => `Last activity ${date}`,
  },

  /**
   * FIL-6: verification is displayed and copyable to **both** sides of the
   * space. Bilateral, so this is not an uploader's tool.
   */
  hash: {
    copy: (fileName: string) => `Copy the full SHA-256 of ${fileName}`,
    /**
     * UPL-7: the client's hash disagreed with the server's.
     *
     * **The red dot's accessible name, and the only place this fact renders.**
     * It used to also flag under the file name, as a separate, visible label —
     * redundant once the dot itself carried the status, so that second flag was
     * removed and this is the one thing left saying "mismatch" on the row.
     *
     * **No explanatory sentence anywhere, at the product's request.** The dot's
     * hover (`verification.mismatchHover`) carries the server's hash; why a
     * mismatch leaves the file downloadable rather than blocking it (UPL-7) is
     * reasoning that lives in `FolderView.tsx`'s doc comments, not on screen.
     */
    mismatch: 'Integrity mismatch',
  },

  /**
   * FIL-6's dot, and the hover it carries the full information in.
   *
   * **Three states, on `hashStatus`.** Pending and mismatch render as plain
   * `<span title>`s — nothing to copy yet, or already flagged under the file
   * name — and only the verified state is a button. `okHover` and
   * `mismatchHover` take the hash itself, because the hover is where the
   * value this column no longer prints as text still lives.
   */
  verification: {
    pending: 'Hash pending',
    okHover: (hash: string) => `Verified · ${hash}`,
    mismatchHover: (hash: string) => `Integrity mismatch · server computed ${hash}`,
  },

  /**
   * FIL-5: uploader provenance, shown to both sides.
   *
   * Rendered on two lines — person, then agency — because a single long
   * `person · agency` string is the hardest cell in the table to scan. The
   * joined form stays for accessible names, where one string is right.
   */
  addedBy: (name: string, agencyName: string) => `${name} · ${agencyName}`,

  /**
   * FIL-8a. The name of a browser-renderable image opens the viewer rather
   * than downloading, so the label on that same control has to say so.
   */
  view: {
    action: (fileName: string) => `View ${fileName}`,
  },

  download: {
    action: (fileName: string) => `Download ${fileName}`,
    label: 'Download',
    working: 'Preparing…',
    failed: 'That file could not be downloaded.',
  },

  /**
   * FIL-10. Offered only where the API said so (`canDelete`), and confirmed
   * because it takes something away — the same line `MembersTab` draws between
   * revoke and reactivate.
   *
   * **No explanatory sentence in the confirm step**, at the product's request —
   * it used to name where the file goes and for how long (UPL-10), but the
   * `label` below already says "recycle bin" before the confirm is even
   * reached, and `Confirm move` says the same on the button itself.
   */
  /**
   * **The label names where the file goes, not the verb the endpoint uses.**
   * "Delete" is ambiguous in a shared space between two agencies — it could mean
   * gone for everyone, gone from our view, or recoverable — and the reader has
   * to know which before they press it, not after. FIL-10 sends the file to the
   * uploading agency's recycle bin, restorable for 30 days (UPL-10), so that is
   * what it says. The route is still `DELETE /files/{id}`; this is the label.
   */
  delete: {
    action: (fileName: string) => `Move ${fileName} to the recycle bin`,
    label: 'Move to recycle bin',
    confirm: 'Confirm move',
    working: 'Moving…',
    cancel: 'Cancel',
    failed: 'That file could not be moved to the recycle bin.',
    /**
     * The overflow trigger. It held delete alone when it was written, and said
     * "actions" rather than "delete" because it was "where a second row-level
     * act would go, and a name that already says 'more actions' does not need
     * to change the day one arrives". That day was RET-7's, and it did not.
     */
    more: (fileName: string) => `More actions for ${fileName}`,
  },

  /**
   * FIL-3 — creating another folder in this space, moved into this popover from
   * `FolderDestinations`' cross-space picker (`folderDestinations.copy.ts` held
   * these strings until then). The space is implicit now, so `open` is the one
   * word that changed on the move: it used to say "New folder", and this panel
   * is already standing inside one, so "New subfolder" is what names the act
   * from here.
   *
   * Offered on `isAdmin && canWrite`, mirroring the old picker's own visibility
   * rule for a space's roots. The refusals are fallbacks — a 409 is the one
   * failure with a specific fix, and `duplicate` says so.
   */
  newFolder: {
    open: 'New subfolder',
    label: 'Folder name',
    submit: 'Create folder',
    submitting: 'Creating…',
    cancel: 'Cancel',
    duplicate: 'This space already has a folder with that name.',
    failed: 'That folder could not be created.',
  },

  /**
   * FIL-3a and FIL-3b — the folder itself, in the controls popover beside NTF-1's
   * switch and RET-7's bulk picker.
   *
   * **Offered only where `folder.canManage` says so**, which is the agency that
   * created the folder with write access, and is neither `canWrite` nor
   * `canSetRetention`: a member may upload into a folder they may not rename, and
   * an admin of the space's *owner* agency may not rename another member's folder
   * at all (ADR-0074). This is the third distinct gate in this panel, which is
   * why it is a third block rather than a clause on one of the others.
   *
   * **Deliberately duplicated from `spaceRoot.copy.ts`'s `manage`.** Both
   * surfaces offer the same two acts and must not disagree about the four words,
   * and a copy file is never imported into another component — this workspace's
   * standing convention, the same one the agency-type labels follow across three
   * surfaces. The strings differ in one place: this panel already knows which
   * folder it is on, so nothing here takes a name.
   *
   * The refusals are fallbacks. A 403 names the agency rule and a 409 names both
   * halves of the delete's remedy, and those are what the reader is shown.
   */
  manage: {
    label: 'Manage',
    rename: 'Rename',
    delete: 'Delete folder',
    nameLabel: 'New name',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    confirm: 'Delete this folder?',
    confirmAction: 'Confirm delete',
    deleting: 'Deleting…',
    renameFailed: 'That folder could not be renamed.',
    deleteFailed: 'That folder could not be deleted.',
  },

  /**
   * RET-4..RET-8. Offered only where the API said so (`canSetRetention`), which
   * is **narrower than `canDelete`**: a member may move their own upload to the
   * bin and may never retain one, because retention is a purchase against the
   * agency's plan (PERM-6). RET-5's Evidence gate sits a level above this —
   * `FolderView`'s own `showRetention`, not a copy key — since it decides
   * whether the whole column exists, not what any string in it says.
   *
   * **No confirm step.** Setting a class takes nothing away — it is
   * `MembersTab`'s line again — so the row's `Select` firing *is* the act,
   * immediate rather than staged behind an Apply/Cancel pair.
   */
  retention: {
    /** Names the picker itself, since it stands alone in its own column. */
    picker: (fileName: string) => `Retention class for ${fileName}`,
    failed: 'That file’s retention could not be changed.',
    /**
     * The class labels. `None` is said as what it means to a reader — the file
     * is on the ordinary window — rather than as the enum's own word.
     */
    classes: {
      None: 'No retention',
      Extend: 'Extend · 1 year',
      Preserve: 'Preserve · 3 years',
      Statutory: 'Statutory · 7 years',
    },
    menu: {
      None: 'No Retention',
      Extend: '1 Year',
      Preserve: '3 Year',
      Statutory: '7 Year',
    },
    until: (date: string) => `Retained until ${date}`,

    /**
     * RET-7's folder-level convenience: the same `Select`, seeded with no
     * standing value rather than a class, on the "trigger, not a choice"
     * reasoning the invite drawer's own space picker already uses — this
     * one applies on selection and then resets, since there is no single class
     * "the folder" is in to show back.
     */
    bulk: {
      label: 'Set folder retention',
      failed: 'The folder’s retention could not be changed.',
      /**
       * The report. It is a `role="status"` and never an alert: rows succeeded,
       * and a partial result is the designed outcome rather than a failure.
       */
      applied: (count: number) =>
        count === 1 ? '1 file updated' : `${String(count)} files updated`,
      skipped: (count: number) =>
        count === 1 ? '1 file skipped' : `${String(count)} files skipped`,
      skippedWhy: 'Skipped files are the counterpart’s, or already in this class.',
      failedHeading: (count: number) =>
        count === 1
          ? '1 file could not be changed:'
          : `${String(count)} files could not be changed:`,
    },
  },

  /**
   * RET-9's retrieval, in the reader's words rather than Azure's.
   *
   * **"Retrieval" and "archived", never "rehydrate" and "Archive tier".** The
   * reader is a defender on a discovery deadline, not an Azure administrator,
   * and `retention.classes.None` already set the precedent: say what it means to
   * the person reading it. The internal vocabulary stays in the code, where the
   * audience is a developer.
   *
   * **"A few hours" is the only duration anywhere here**, because Azure supplies
   * no ETA (RET-9's "hours of latency" is what was sold, and it is all we know).
   * A countdown would be a promise the system cannot keep.
   */
  rehydration: {
    label: 'Request retrieval',
    working: 'Requesting…',
    action: (fileName: string) => `Request retrieval for ${fileName}`,
    started: 'Retrieval started. This file will be downloadable in a few hours.',
    /** The answer to a request for a file that is already back. Not a failure. */
    available: 'That file is already available. Try downloading it again.',
    failed: 'Retrieval could not be requested for that file.',
    /**
     * The badge under an archived file's name.
     *
     * **Only these two states get one.** `Hot`, `Cool`, and `Cold` render
     * nothing: the badge exists to explain a missing Download button, and a
     * `COLD` chip on every Preserve row would be the ghost of the per-file tier
     * control §14 forbids — a tier a reader can see but must never choose.
     */
    badge: {
      Archive: 'Archived',
      Rehydrating: 'Retrieving',
    },
  },

  /**
   * The "expiring soon" hint under a file's name (RET-3 as amended, RET-12).
   *
   * A room can shorten how long files are kept; when a file is within the
   * room's warn lead of that, its row says so. "Expires today" for a file
   * already at or past its window — clamped, never a negative count — and it is
   * still restorable for 30 days after the sweep takes it, so the word is
   * "expires", not "deleted".
   */
  expiring: {
    soon: (days: number) => (days <= 0 ? 'Expires today' : `Expires in ${String(days)}d`),
  },

  /** Bytes, at the scale evidence actually arrives in (UPL-3). */
  units: ['B', 'KB', 'MB', 'GB', 'TB'],

  states: {
    loading: 'Loading this folder…',
    empty: 'No files in this folder yet.',
    /**
     * The same emptiness, to a reader whose agency is scoped to `own` (CON-24,
     * FIL-2 as amended).
     *
     * **`empty` is a claim this reader cannot check, which is why it needed
     * replacing rather than qualifying.** "No files in this folder yet" is a
     * statement about the folder; an `own`-scope member is looking at a filtered
     * view of it, and a folder everybody else is filling can read as abandoned to
     * the one agency that has not put anything in it. That is a person concluding
     * the product is broken, or worse, that nobody has followed the instruction
     * they are all following.
     *
     * **Two sentences, and the second is the one that had to be written
     * carefully.** It states the rule that applies to this reader and stops.
     * It names no other agency, gives no count, and does not say that anything
     * is here — every one of those would disclose exactly what CON-24 withholds,
     * and a count is the leak ADR-0069 closed on the folder card.
     *
     * Nothing here apologises. The arrangement is deliberate and the reader's own
     * agency agreed to be in the room under it, so this reads like `readOnly`
     * below — a description of how the space is arranged, not a fault.
     */
    emptyOwnScope:
      'No files from your agency in this folder yet. In this space you see only your own agency’s uploads.',
    /**
     * A filter narrowed the table to zero rows — distinct from `empty` and
     * `emptyOwnScope` above, both of which are claims about the *folder*.
     * This one is a claim about the *view*: the folder is not empty, this
     * agency simply put nothing in it. Names the agency, on the same
     * "state the fact plainly" rule the rest of this file follows, and says
     * how to see the rest rather than leaving the reader to find "All
     * agencies" back in the panel unprompted.
     *
     * Reachable without any race: filter to an agency, then have its one
     * remaining file deleted (by either side) — the filter stays applied
     * across the refetch even after the filter trigger itself has since
     * hidden, because a folder with one uploading agency left has nothing
     * to filter (`showAgencyFilter`, `FolderView.tsx`).
     */
    emptyFiltered: (agencyName: string) =>
      `No files from ${agencyName} in this folder. Choose All agencies to see the rest.`,
    /**
     * CON-13: the receiving side of a space browses and downloads, and that is the
     * whole arrangement rather than a limitation to apologise for.
     */
    readOnly: 'Your agency can download from this space, but not add to it.',
    tableCaption: 'Files in this folder',
    /**
     * The page's `<h1>` when the *space* call failed but the file table loaded —
     * a designed state, because that call is supplementary and must never gate
     * the files.
     *
     * It renders directly under the `Shared Folder` eyebrow, so it reads
     * `SHARED FOLDER / Name unavailable`. Deliberately not "Unnamed folder", which
     * would claim the folder has no name when the truth is that we could not
     * load it; and deliberately not a full error sentence, which would overstate
     * a failure on a screen whose table works fine.
     */
    unnamedFolder: 'Name unavailable',
    error: 'This folder could not be loaded.',
    forbidden: 'You do not have access to this folder.',
    missing: 'This folder does not exist, or it is no longer shared with your agency.',
    retry: 'Try again',
  },

  /**
   * The trigger over the switch and the bulk retention picker — `RowActions`'
   * pattern, one level up: Upload is this header's one loud act, so the two
   * standing controls that used to sit beside it now live behind an icon,
   * the same move that keeps a row's overflow to one visually quiet trigger.
   */
  folderControls: {
    more: 'Folder settings',
    /**
     * Heads the pin and notification toggles — the panel's top group, and the
     * one that went the longest without a label. Retention and Manage each
     * already had one below it; two labelled groups and one bare pair read as
     * the pair belonging to neither.
     */
    preferences: 'Preferences',
  },

  /**
   * CON-24's filter — its own popover beside "Folder settings," not a fourth
   * group inside it: that panel holds preferences, retention, and manage,
   * every one of them a folder fact or a standing preference, and a filter
   * changes what the table shows rather than either. Offered only where
   * `FolderView`'s own `showAgencyFilter` says so — an `own`-scope reader,
   * or a folder with one uploading agency, has nothing this control could
   * narrow.
   */
  filter: {
    trigger: 'Filter by agency',
    /**
     * The trigger's accessible name once a filter is applied, so a screen
     * reader announces which agency is active rather than always "Filter by
     * agency" regardless of state — `folderControls.more` never needed this
     * because "Folder settings" names no state to begin with.
     */
    activeTrigger: (agencyName: string) => `Filtered to ${agencyName}`,
    /** Heads the panel, on `folderControls.preferences`'s precedent. */
    heading: 'Agency',
    /** The leading, clearing option (CON-24) — always first, never absent. */
    all: 'All agencies',
  },

  /**
   * The notifications switch and its one-time consent dialog (NTF-1, NTF-4).
   *
   * **The statement itself is deliberately absent from this file, and that is
   * the rule most likely to be broken here.** It is the server's words and the
   * dialog renders exactly what `GET /me/notifications` returned. A copy in
   * this workspace would be a second wording free to disagree with the one an
   * acceptance is recorded against — and a stale bundle would show text this
   * system had already replaced. Same rule as `Signature`'s clickwrap.
   *
   * The switch's label says what turning it on *does*, not what it is, because
   * a control named "Notifications" leaves a reader to guess about what and
   * from whom.
   */
  notifications: {
    label: 'Notify me when this folder changes',
    dialog: {
      title: 'Notifications for this folder',
      accept: 'I have read and agree to this.',
      confirm: 'Turn on notifications',
      cancel: 'Cancel',
    },
    /**
     * The fallback only. `messageFrom` prefers the API's own line, which for a
     * statement that changed mid-session names the actual remedy — read it
     * again — where this would only say something went wrong.
     */
    failed: 'Notifications could not be changed. Try again.',
  },

  dateLocale: 'en-US',
} as const;
